import { FunctionComponent, useMemo, useRef } from "react";

import { Button } from "primereact/button";
import { classNames } from "primereact/utils";

import { IconProp } from "@fortawesome/fontawesome-svg-core";

import { TooltipWrapper } from "../../Components/TooltipWrapper/TooltipWrapper";
import { Icon } from "../../Icon/Icon";

import "./ActionButton.scss";

interface IActionButtonProps {
  id: string;
  label?: string;
  icon?: IconProp;
  isLoading?: boolean;
  tooltip?: string;
  tooltipPosition?: "top" | "bottom" | "left" | "right" | "mouse";
  tooltipDisabled?: boolean;
  buttonDisabled?: boolean;
  showBackground?: boolean;
  focused?: boolean;
  state?: "default" | "warning" | "danger";
  removeSpaces?: boolean;
  hide?: boolean;
  active?: boolean;
  centeredHorizontally?: boolean;
  fullWidth?: boolean;
  customClasses?: string;
  onClick?: (
    event: React.MouseEvent<HTMLButtonElement> | React.KeyboardEvent<HTMLButtonElement>,
  ) => void | Promise<void | (() => void)>;
}

export const ActionButton: FunctionComponent<IActionButtonProps> = (props: IActionButtonProps) => {
  const tooltipRef = useRef<HTMLDivElement>(null);

  const iconOnly = !props.label && props.icon;

  const iconTone = useMemo(() => {
    if (props.buttonDisabled && !props.showBackground) {
      return "muted";
    }

    if (!props.active && !props.showBackground) {
      return "default";
    }

    return "inverse";
  }, [props.showBackground, props.buttonDisabled, props.active]);

  return (
    <div ref={tooltipRef} className={classNames(props.customClasses, "action-button-wrapper")}>
      {/* TooltipWrapper is used instead of Button.tooltip because native button tooltips do not show on disabled buttons */}
      <TooltipWrapper
        targetRef={tooltipRef}
        content={props.tooltip ?? ""}
        position={props.tooltipPosition ?? "right"}
        disabled={props.tooltipDisabled || props.hide}
      />

      <Button
        type="button"
        className={classNames(
          "action-button",
          {
            active: props.active,
            "icon-only": iconOnly,
            "text-only": props.label && !props.icon,
            "hide-background": !props.showBackground,
            focused: props.focused,
            "remove-spaces": props.removeSpaces,
            hide: props.hide,
            disabled: props.buttonDisabled,
            "center-horizontally": props.centeredHorizontally,
            "full-width": props.fullWidth,
            danger: props.state === "danger",
            warning: props.state === "warning",
          },
          props.customClasses || "",
        )}
        onClick={!props.buttonDisabled ? props.onClick : undefined}
        onKeyDown={event => !props.buttonDisabled && event.key === "Enter" && props.onClick?.(event)}
        disabled={props.buttonDisabled}
        id={props.id}
        data-testid={props.id}
        icon={props.icon ? <Icon icon={props.icon} color={iconTone} iconSize={iconOnly ? "md" : "sm"} /> : undefined}
        label={props.label || undefined}
        loading={props.isLoading}
      />
    </div>
  );
};
